import { useEffect, useEffectEvent, useRef } from 'react';

import { useAtomValue } from 'jotai';

import { audioQueueAtom } from '@/shared-survey';
import { currentPlayingIdAtom } from '@/shared-ui';

/**
 * Hard timeout: if TTS never drains (audio error, stuck queue), we stop
 * waiting and fire `onReady` anyway so the user isn't trapped on a
 * "still thinking" screen. Breadcrumb is emitted on this path so we can
 * monitor how often it fires in production.
 */
const HARD_TIMEOUT_MS = 15_000;

interface UseWaitForConversationEndOptions {
  /** True once the BE signals `isConversationOver`. */
  isConversationOver: boolean;
  /** True while we're still recording. Wait is gated on this. */
  isRecording: boolean;
  /** Called exactly once when TTS has drained (or on hard timeout). */
  onReady: () => void;
  /**
   * Trailing buffer after TTS finishes before firing `onReady`. Captures any
   * brief post-audio pause on the recording. Defaults to 0.
   */
  trailingBufferMs?: number;
  /**
   * Optional override: some flows (no AI voice) don't gate on TTS at all.
   * Defaults to true.
   */
  waitForTts?: boolean;
}

/**
 * Waits for the conversation to fully end before calling `onReady`:
 * 1. `isConversationOver` goes true
 * 2. (optional) TTS queue drains: `!isSpeaking && !hasPendingAudio`
 * 3. trailing buffer elapses
 * 4. `onReady` fires ONCE (ref-based one-shot latch)
 *
 * A 15s hard timeout from `isConversationOver` guarantees `onReady` fires
 * even if audio is stuck.
 */
export function useWaitForConversationEnd({
  isConversationOver,
  isRecording,
  onReady,
  trailingBufferMs = 0,
  waitForTts = true,
}: UseWaitForConversationEndOptions): void {
  const currentPlayingId = useAtomValue(currentPlayingIdAtom);
  const audioQueue = useAtomValue(audioQueueAtom);
  const isSpeaking = currentPlayingId !== null;
  const hasPendingAudio = audioQueue.some(
    (i) =>
      i.status === 'pending' ||
      i.status === 'loading' ||
      i.status === 'ready' ||
      i.status === 'playing'
  );

  // One-shot latch. Flips true as soon as we schedule `onReady` (via the
  // trailing-buffer timer OR the hard timeout). Never resets, so re-renders
  // and strict-mode double-invoke can't cause a duplicate call.
  const firedRef = useRef(false);
  const trailingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hardTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fireOnce = useEffectEvent((reason: 'ready' | 'timeout') => {
    if (firedRef.current) return;
    firedRef.current = true;
    if (reason === 'timeout') {
      console.warn(
        '[useWaitForConversationEnd] hard timeout fired — TTS queue did not drain within 15s'
      );
    }
    onReady();
  });

  // Hard timeout: guarantees onReady fires even if TTS never drains.
  useEffect(() => {
    if (!isConversationOver || !isRecording || firedRef.current) return;

    hardTimeoutRef.current = setTimeout(() => {
      hardTimeoutRef.current = null;
      fireOnce('timeout');
    }, HARD_TIMEOUT_MS);

    return () => {
      if (hardTimeoutRef.current) {
        clearTimeout(hardTimeoutRef.current);
        hardTimeoutRef.current = null;
      }
    };
  }, [isConversationOver, isRecording]);

  // Watch TTS drain + schedule the trailing-buffer fire.
  useEffect(() => {
    if (!isConversationOver || !isRecording || firedRef.current) return;

    const audioDrained = !waitForTts || (!isSpeaking && !hasPendingAudio);
    if (!audioDrained) {
      // Audio came back — clear any pending trailing timer; the effect re-runs
      // when isSpeaking/hasPendingAudio change.
      if (trailingTimerRef.current) {
        clearTimeout(trailingTimerRef.current);
        trailingTimerRef.current = null;
      }
      return;
    }

    if (trailingTimerRef.current) return;

    if (trailingBufferMs > 0) {
      trailingTimerRef.current = setTimeout(() => {
        trailingTimerRef.current = null;
        fireOnce('ready');
      }, trailingBufferMs);
    } else {
      fireOnce('ready');
    }

    return () => {
      if (trailingTimerRef.current) {
        clearTimeout(trailingTimerRef.current);
        trailingTimerRef.current = null;
      }
    };
  }, [
    isConversationOver,
    isRecording,
    isSpeaking,
    hasPendingAudio,
    trailingBufferMs,
    waitForTts,
  ]);

  // Cleanup on unmount.
  useEffect(
    () => () => {
      if (trailingTimerRef.current) clearTimeout(trailingTimerRef.current);
      if (hardTimeoutRef.current) clearTimeout(hardTimeoutRef.current);
    },
    []
  );
}
